import { MeasurementUnitSystem } from '@/hooks/useMeasurementUnitSystem'
import { formatDecimal } from './number-format'

const UNIT_CONVERSION_ALLOWED = ['°C', '°F', 'cm', 'in', 'kg', 'lb']

function normalizeNumberInput(value: string | number | null): number | null {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'number') return value

  // Replace comma with period to handle different locale decimal separators
  const normalized = value.toString().replace(',', '.')
  const parsed = parseFloat(normalized)

  return isNaN(parsed) ? null : parsed
}

function toImperialUnit(unit: string) {
  switch (unit) {
    case '°C':
      return '°F'
    case 'cm':
      return 'in'
    case 'kg':
      return 'lb'
    default:
      return unit
  }
}

function toMetricUnit(unit: string) {
  switch (unit) {
    case '°F':
      return '°C'
    case 'in':
      return 'cm'
    case 'lb':
      return 'kg'
    default:
      return unit
  }
}

function parseImperialValue(unit: string, value: string | number | null) {
  const numValue = normalizeNumberInput(value)
  if (numValue === null) return ''

  const imperialUnit = toImperialUnit(unit)

  if (imperialUnit === unit) return numValue

  switch (imperialUnit) {
    case '°F':
      return (numValue * 9) / 5 + 32
    case 'in':
      return numValue / 2.54
    case 'lb':
      return numValue / 0.45359237
    default:
      return numValue
  }
}

function parseMetricValue(unit: string, value: string | number | null) {
  const numValue = normalizeNumberInput(value)
  if (numValue === null) return ''

  const metricUnit = toMetricUnit(unit)

  if (metricUnit === unit) return numValue

  switch (metricUnit) {
    case '°C':
      return ((numValue - 32) * 5) / 9
    case 'cm':
      return numValue * 2.54
    case 'kg':
      return numValue * 0.45359237
    default:
      return numValue
  }
}

export function toMeasurementUnit(unit: string, unitSystem: MeasurementUnitSystem) {
  switch (unitSystem) {
    case 'metric':
      return toMetricUnit(unit)
    case 'imperial':
      return toImperialUnit(unit)
    default:
      return unit
  }
}

export function parseMeasurementValue(unit: string, value: string | number | null, unitSystem: MeasurementUnitSystem) {
  // Only convert units that need conversion (temperature, height, weight)
  if (!UNIT_CONVERSION_ALLOWED.includes(unit)) {
    return value?.toString() ?? ''
  }

  let parsedValue = value
  switch (unitSystem) {
    case 'metric':
      parsedValue = parseMetricValue(unit, value)
      break
    case 'imperial':
      parsedValue = parseImperialValue(unit, value)
      break
    default:
      break
  }
  return parsedValue !== '' && parsedValue !== null ? formatDecimal(parsedValue, 2) : ''
}
